import type { PointNumber, TableRules } from '../types.js';

/**
 * Max odds-bet amount allowed by the table's `oddsCap` for a `pass` or `come`
 * parent of the given size and number.
 *
 * The cap multiplier depends on the parent's number:
 *  - 3-4-5x: 3 on 4/10, 4 on 5/9, 5 on 6/8 — the standard Vegas rule, chosen
 *    because each tier yields the same max win (6×bet) given pass-odds payouts.
 *  - 1x / 2x / 5x / 10x / 100x: a flat multiplier on every number.
 *
 * `parentAmount` and the returned cap are integer cents.
 */
export function maxOddsForLineBet(
  parentAmount: number,
  point: PointNumber,
  oddsCap: TableRules['oddsCap'],
): number {
  return parentAmount * passMultiplier(point, oddsCap);
}

/**
 * Max don't-odds (lay) amount. Cap rule pins the WIN, not the lay stake:
 * every multiplier tier governs how much the lay can return on a 7. We invert
 * the lay-payout ratio (1:2 on 4/10, 2:3 on 5/9, 5:6 on 6/8) so a 3-4-5x cap
 * resolves to a flat 6×parent max lay across all numbers.
 */
export function maxDontOddsForLineBet(
  parentAmount: number,
  point: PointNumber,
  oddsCap: TableRules['oddsCap'],
): number {
  const maxWin = parentAmount * passMultiplier(point, oddsCap);
  // Lay = win × (stake-to-win ratio). 4/10 lays 2:1, 5/9 lays 3:2, 6/8 lays 6:5.
  if (point === 4 || point === 10) return maxWin * 2;
  if (point === 5 || point === 9) return Math.floor(maxWin * 3 / 2);
  return Math.floor(maxWin * 6 / 5);
}

function passMultiplier(point: PointNumber, oddsCap: TableRules['oddsCap']): number {
  switch (oddsCap) {
    case '1x':   return 1;
    case '2x':   return 2;
    case '3-4-5x':
      if (point === 4 || point === 10) return 3;
      if (point === 5 || point === 9)  return 4;
      return 5;
    case '5x':   return 5;
    case '10x':  return 10;
    case '100x': return 100;
  }
}
